'use client';

import { useCallback } from 'react';
import { useSocketManagerContext } from '../providers/context';
import NatsManager from '../library/nats-manager';
import type { NatsMessageCallback } from '../types';

/**
 * Access NATS connection and subscribe to subjects.
 *
 * @example
 * const { isConnected, subscribe } = useNats();
 *
 * useEffect(() => {
 *   if (!isConnected) return;
 *   const unsub = subscribe('prices.NSE.RELIANCE', (data, err) => {
 *     if (err) return;
 *     setPrice(data.live_price);
 *   });
 *   return unsub;
 * }, [isConnected, subscribe]);
 */
export function useNats() {
  const context = useSocketManagerContext();

  const subscribe = useCallback(
    (subject: string, callback: NatsMessageCallback): (() => void) => {
      if (!context.isNatsConnected) {
        console.warn('[useNats] NATS not connected, subscription may fail');
      }

      const natsManager = NatsManager.getInstance();
      return natsManager.subscribe(subject, callback);
    },
    [context.isNatsConnected],
  );

  return {
    connection: context.natsConnection,
    isConnected: context.isNatsConnected,
    subscribe,
  };
}

/**
 * Get NATS connection status only. Lighter than useNats when you don't need subscribe.
 *
 * @example
 * const { status, isConnected } = useNatsStatus();
 * // status: 'connected' | 'draining' | 'closed'
 */
export function useNatsStatus() {
  const { natsConnection, isNatsConnected } = useSocketManagerContext();

  const getStatus = useCallback(() => {
    if (!natsConnection) {
      return 'closed';
    }
    if (natsConnection.isDraining()) {
      return 'draining';
    }
    return natsConnection.isClosed() ? 'closed' : 'connected';
  }, [natsConnection]);

  return {
    isConnected: isNatsConnected,
    status: getStatus(),
  };
}
